import { sentenceCase } from "@portal/app/_common/lib/changeCase";
import {
  isWellFormedDreamSearchZip,
  normalizeDreamSearchZip,
} from "@portal/app/_common/lib/dreamSearchRoutes";
import { Filters } from "@portal/app/_common/types/dreamSearch";

export const DEFAULT_SEARCH_PAGE_SIZE = 25;

export const getDefaultDreamSearchVariables = ({
  industry,
  subindustries,
  vocation,
  programLevels,
  excludeRequirements,
  onlyIncludePartnered,
  geoCenter,
  first,
  complianceExclusionTags,
  excludedProgramIds,
}: {
  industry?: GQL.VocationIndustry;
  subindustries?: GQL.VocationSubindustry[] | null;
  vocation?: {
    id: string;
    name?: string;
    industry?: GQL.VocationIndustry;
  } | null;
  programLevels?: GQL.ProgramLevel[] | null;
  excludeRequirements?: GQL.EligibilityRequirement[] | null;
  onlyIncludePartnered?: boolean;
  geoCenter: {
    zip?: string | null;
    city?: string | null;
    state?: string | null;
    geocodeId?: string | null;
  };
  first?: number | undefined;
  complianceExclusionTags: string[] | undefined;
  excludedProgramIds?: string[];
}) => ({
  dreamSearchFilters: {
    ...(excludedProgramIds?.length && {
      program_exclude_id_in: excludedProgramIds,
    }),
    ...(subindustries?.length && {
      program_vocation_subindustries_in: subindustries,
    }),
    ...(vocation && {
      program_vocation_in: [vocation.id],
      ...(vocation.industry && { program_vocation_industry: [vocation.industry] }),
    }),
    ...(programLevels?.length && {
      program_level_in: programLevels,
    }),
    ...(excludeRequirements?.length && {
      program_exclude_requirement: excludeRequirements,
    }),
    ...(industry && {
      program_vocation_industry: [industry],
    }),
    ...(onlyIncludePartnered && {
      provider_only_partnered: onlyIncludePartnered,
    }),
  },
  geo: {
    geoCenter,
  },
  first: first || DEFAULT_SEARCH_PAGE_SIZE,
  complianceExclusionTags: complianceExclusionTags ?? [],
});

/* display the city, state, and zip, depending on what data is available.
  priority for what to show is as follows:
  1. zip/state/city from dreamSearchItemsConnection query
  2. zip/state/city from geoCenter (cloudflare)
  3. fallback zip from geoCenter (see getGeoCenterFromRequest.)
  */
export const getCityStateAndZip = (
  connectionCity: string | undefined | null,
  connectionState: string | undefined | null,
  connectionZip: string | undefined | null,
  geoCenter: Omit<GQL.DreamSearchGeoCenter, "geocodeId">,
  landingPage?: { zip?: string | null }
) => {
  let zip = "";
  const state = "";
  const city = "";
  if (connectionZip && connectionState && connectionCity) {
    return { zip: connectionZip, state: connectionState, city: connectionCity };
  } else if (geoCenter.zip && geoCenter.state && geoCenter.city) {
    return { zip: geoCenter.zip, state: geoCenter.state, city: geoCenter.city };
  }

  if (!zip) {
    if (landingPage?.zip) {
      zip = landingPage.zip;
    } else if (geoCenter.zip) {
      zip = geoCenter.zip;
    }
  }

  return { zip, state, city };
};

export const genFilterAnalyticsMixpanelArgs = (
  filterGroup: keyof GQL.DreamSearchFilters, // what type of filter got activated
  filterItem: string | number, // which specific filter got activated
  filters: Filters // the resulting filters active after a filter is activated
) => ({
  event_name: "Dream Search - Filter" as const,
  properties: { filterGroup, filterItem, filters },
});

export const getIndustryAdjectiveForTitle = (industry: GQL.VocationIndustry) => {
  if (industry === "TECHNICAL_AND_COMMUNICATIONS") {
    return "Technology";
  } else if (industry === "HEALTH") {
    return "Healthcare";
  } else {
    return sentenceCase(industry);
  }
};

/**
 * Copy for the page-level DreamSearch heading. The subject and location are
 * separate so the location can be the editable control inside the <h1>, while
 * still appearing only once to users and crawlers.
 */
export const getDreamSearchPageHeadingParts = ({
  industry,
  searchCity,
  searchState,
  searchZip,
  suppressLocation = false,
}: {
  industry?: GQL.VocationIndustry;
  searchCity?: string;
  searchState?: string;
  searchZip?: string;
  suppressLocation?: boolean;
}) => {
  const subject = industry
    ? `Partnered ${getIndustryAdjectiveForTitle(industry).toLowerCase()} programs`
    : "Partnered programs";
  const normalizedZip = normalizeDreamSearchZip(searchZip);
  const validZip = isWellFormedDreamSearchZip(normalizedZip) ? normalizedZip : undefined;
  const location = suppressLocation
    ? undefined
    : searchCity && searchState
      ? `${searchCity}, ${searchState}${validZip ? ` ${validZip}` : ""}`
      : validZip;

  return { subject, location };
};

/** Whether the initial request lacks a genuine ZIP and should show the entry form. */
export const shouldEditDreamSearchLocationInitially = ({
  initialZip,
  shouldPromptForLocation = false,
}: {
  initialZip?: string;
  shouldPromptForLocation?: boolean;
}) => {
  const normalizedZip = normalizeDreamSearchZip(initialZip);
  return shouldPromptForLocation || !isWellFormedDreamSearchZip(normalizedZip);
};

/** Whether dismissing the ZIP form would reveal a genuine editable location. */
export const canDismissDreamSearchLocationPrompt = ({
  searchCity,
  searchState,
  searchZip,
  shouldPromptForLocation = false,
}: {
  searchCity?: string;
  searchState?: string;
  searchZip?: string;
  shouldPromptForLocation?: boolean;
}): boolean =>
  !shouldPromptForLocation &&
  !!getDreamSearchPageHeadingParts({ searchCity, searchState, searchZip }).location;

/**
 * Label for the DreamSearch results <h2>. Describes the list that follows it, so it stays
 * distinct from the page <h1> ("Partnered [industry] programs in [location]").
 */
export const getResultsHeading = ({
  industry,
  searchCity,
  searchState,
  searchZip,
}: {
  industry?: GQL.VocationIndustry;
  searchCity?: string;
  searchState?: string;
  searchZip?: string;
}) => {
  const subject = industry ? `${getIndustryAdjectiveForTitle(industry)} programs` : "Programs";
  const location = searchCity && searchState ? `${searchCity}, ${searchState}` : searchZip;

  return location ? `${subject} near ${location}` : subject;
};
